import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { CarbonCredit } from 'src/app/shared/interfaces/carbon-credit';

import { CharacteristicConsultService } from '../services/characteristic-consult.service';
import {
  CarbonCreditTypeBadge,
  displayValue,
  formatComitenteDocument,
  isAcxCredit,
  isExternalUrl,
  resolveCarbonCreditTypeBadge,
} from '../shared/characteristic-consult-details.utils';

@Component({
  standalone: false,
  selector: 'app-characteristic-consult-details-drawer',
  templateUrl: './characteristic-consult-details-drawer.component.html',
  styleUrls: ['./characteristic-consult-details-drawer.component.scss'],
})
export class CharacteristicConsultDetailsDrawerComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() carbonCreditCode: string | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() loadFailed = new EventEmitter<void>();

  credit: CarbonCredit | null = null;
  isLoading = false;

  private loadSubscription?: Subscription;

  constructor(
    private readonly characteristicConsultService: CharacteristicConsultService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true || changes['carbonCreditCode']) {
      this.loadDetailsIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  get typeBadge(): CarbonCreditTypeBadge {
    return resolveCarbonCreditTypeBadge(this.credit ?? ({} as CarbonCredit));
  }

  get instrumentType(): string {
    return displayValue(this.credit?.financialInstrumentTypeCode);
  }

  get issueDate(): string {
    return displayValue(this.credit?.issueDate);
  }

  get comitenteDocument(): string {
    return formatComitenteDocument(this.credit?.comitenteDocumentNumber);
  }

  get issuedQuantity(): string {
    return displayValue(this.credit?.creditInitialQuantity);
  }

  get currentQuantity(): string {
    return displayValue(this.credit?.creditQuantity);
  }

  get vintageStart(): string {
    return displayValue(this.credit?.vintageStart);
  }

  get vintageEnd(): string {
    return displayValue(this.credit?.vintageEnd);
  }

  get projectName(): string {
    return displayValue(this.credit?.projectName);
  }

  get projectLink(): string {
    return displayValue(this.credit?.projectLink);
  }

  get certifierName(): string {
    return displayValue(this.credit?.certifierDescription);
  }

  get additionalDescription(): string {
    return displayValue(this.credit?.observationText);
  }

  get hasProjectLink(): boolean {
    return isExternalUrl(this.credit?.projectLink);
  }

  get showBrcerBadge(): boolean {
    return this.credit?.brcerIndicator === true;
  }

  get showAcxBadge(): boolean {
    return this.credit ? isAcxCredit(this.credit) : false;
  }

  close(): void {
    this.closed.emit();
  }

  private loadDetailsIfNeeded(): void {
    if (!this.isOpen || !this.carbonCreditCode?.trim()) {
      return;
    }

    this.isLoading = true;
    this.credit = null;

    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.characteristicConsultService
      .getCarbonCreditById(this.carbonCreditCode, { suppressLegacyErrorModal: true })
      .subscribe({
        next: (credit) => {
          this.credit = credit;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.loadFailed.emit();
        },
      });
  }
}
